import { useEffect } from 'react';
import { announceQuickExit } from './keyShare';

/**
 * "Leave quickly": for someone who needs this off the screen now — a person
 * walking in, or a record that's too much to keep looking at.
 *
 * It signs out the same way AuthContext.logout does (drops the token and
 * cached user from localStorage), and clearing sessionStorage drops the
 * unlocked private key (lib/crypto.js), any shared-access key
 * (lib/sharedAccess.js) and in-progress drafts — so Back, or reopening the
 * site, shows the sign-in page, not the organisations and requests that
 * aren't encrypted. location.replace()
 * swaps this history entry for BBC News rather than adding one, so Back
 * doesn't step straight back onto the page that was open.
 * `#main-content` is BBC's skip-link target: it lands just below their
 * masthead, so it looks like someone was already reading.
 */
export const QUICK_EXIT_URL = 'https://www.bbc.co.uk/news#main-content';

// Where every *other* open tab goes, one picked at random each — a row of
// tabs all on BBC News would look odder than the site did. Ordinary,
// signed-out pages that anyone might have open.
export const ELSEWHERE_URLS = [
  'https://www.bbc.co.uk/weather',
  'https://www.bbc.co.uk/sport',
  'https://www.bbc.co.uk/food/recipes',
  'https://en.wikipedia.org/wiki/Main_Page',
  'https://www.youtube.com/',
  'https://www.google.co.uk/',
  'https://www.metoffice.gov.uk/',
];

const signOutHere = () => {
  try {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    sessionStorage.clear();
  } catch { /* storage blocked — leave anyway */ }
};

export const quickExit = () => {
  // Every other open tab leaves too (lib/keyShare.js) — this has to go
  // before the token does, since it's addressed by who's signed in.
  try { announceQuickExit(); } catch { /* best effort */ }
  signOutHere();
  window.location.replace(QUICK_EXIT_URL);
};

/** What another tab does when told this one quick-exited. */
export const leaveForElsewhere = () => {
  signOutHere();
  window.location.replace(ELSEWHERE_URLS[Math.floor(Math.random() * ELSEWHERE_URLS.length)]);
};

const DOUBLE_ESC_MS = 800;

/**
 * Esc twice in quick succession leaves (the convention on other quick-exit
 * sites). A single Esc still just closes dialogs as normal.
 *
 * Also guards against the back/forward cache: Back into an earlier entry
 * can restore this page from memory with decrypted records still on
 * screen, so a restore after signing out reloads (onto sign-in) instead.
 */
export const useQuickExit = () => {
  useEffect(() => {
    let lastEsc = 0;
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.repeat) return;
      const now = Date.now();
      if (now - lastEsc < DOUBLE_ESC_MS) quickExit();
      lastEsc = now;
    };
    const onPageShow = (e) => {
      if (!e.persisted) return;
      let signedOut = true;
      try { signedOut = !localStorage.getItem('auth_token'); } catch { /* treat as signed out */ }
      if (signedOut) window.location.reload();
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);
};
